import { useState } from "react";
import { motion } from "framer-motion";
import { FileText, AlertTriangle } from "lucide-react";
import { api, type JobStatus } from "../api/client";
import JobProgressIndicator from "../components/JobProgressIndicator";

const REPORT_TYPES = [
  { v: "microbiome_health", l: "Microbiome Health Report" },
  { v: "genomic_variant", l: "Genomic Variant Report" },
  { v: "clinical_study", l: "Clinical Study Report" },
  { v: "research_summary", l: "Research Summary" },
];
const AUDIENCES = ["clinician", "consumer", "researcher"];

export default function ReportPage() {
  const [reportType, setReportType] = useState("microbiome_health");
  const [audience, setAudience] = useState("clinician");
  const [inputJson, setInputJson] = useState(
    '{\n  "taxa": {\n    "akkermansia_muciniphila": 0.1,\n    "faecalibacterium_prausnitzii": 2.5\n  }\n}'
  );
  const [jobStatus, setJobStatus] = useState<JobStatus | "idle">("idle");
  const [elapsedMs, setElapsedMs] = useState(0);
  const [report, setReport] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const submit = async () => {
    let input_data: Record<string, unknown>;
    try {
      input_data = JSON.parse(inputJson);
      if (typeof input_data !== "object" || input_data === null || Array.isArray(input_data)) {
        throw new Error("Input must be a JSON object");
      }
    } catch (e) {
      setError(`Invalid JSON: ${e instanceof Error ? e.message : "parse error"}`);
      return;
    }
    setJobStatus("pending");
    setElapsedMs(0);
    setError(null);
    setReport(null);
    try {
      const res = await api.reportGenerate(
        { report_type: reportType, input_data, audience, output_format: "markdown" },
        (p) => {
          setJobStatus(p.status);
          setElapsedMs(p.elapsed_ms);
        }
      );
      setReport(res.report);
      setJobStatus("completed");
    } catch (e) {
      setError(e instanceof Error ? e.message : "Unknown error");
      setJobStatus("failed");
    }
  };

  const isWorking = jobStatus === "pending" || jobStatus === "running";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold flex items-center gap-2">
          <FileText className="text-nhs-green" /> Report Generation
        </h1>
        <p className="text-sm text-gray-600 mt-1">
          Generate structured medical and scientific reports from your data.
        </p>
      </div>

      <div className="card">
        <div className="grid grid-cols-2 gap-3 mb-3">
          <div>
            <label className="label" htmlFor="report-type">Report type</label>
            <select
              id="report-type"
              className="input"
              value={reportType}
              onChange={(e) => setReportType(e.target.value)}
              disabled={isWorking}
            >
              {REPORT_TYPES.map((r) => (
                <option key={r.v} value={r.v}>
                  {r.l}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="label" htmlFor="audience">Audience</label>
            <select
              id="audience"
              className="input"
              value={audience}
              onChange={(e) => setAudience(e.target.value)}
              disabled={isWorking}
            >
              {AUDIENCES.map((a) => (
                <option key={a} value={a}>
                  {a}
                </option>
              ))}
            </select>
          </div>
        </div>
        <label className="label" htmlFor="input-json">Input data (JSON)</label>
        <textarea
          id="input-json"
          rows={8}
          className="input font-mono text-sm"
          value={inputJson}
          onChange={(e) => setInputJson(e.target.value)}
          disabled={isWorking}
        />
        <button onClick={submit} disabled={isWorking} className="btn-primary mt-3 w-full md:w-auto">
          {isWorking ? "Generating..." : "Generate Report"}
        </button>
      </div>

      <JobProgressIndicator status={jobStatus} elapsedMs={elapsedMs} label="Building your report..." />

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 p-3 rounded flex gap-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" /> {error}
        </div>
      )}

      {report && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="card">
          <h2 className="font-semibold mb-3">Generated report</h2>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap text-gray-800">
            {report}
          </div>
        </motion.div>
      )}
    </div>
  );
}
